import { updatePurchaseDiscount } from './purchase-discount.js';
import { CartErrorEvent } from '@theme/events';
import {
  buildAmerica250CartItems,
  getAmerica250CartConfig,
  getAmerica250CheckoutUrl,
} from '@theme/america-250-cart-config';

const CLOUD_CONFIG_SELECTOR = 'script[data-legacybox-cloud-product-config]';
const PENDING_CLASS = 'product-details--america-250-checkout-pending';
const PENDING_BUTTON_ATTRIBUTE = 'data-america250-pending';
const PENDING_BUTTON_LABEL_ATTRIBUTE = 'data-america250-pending-label';
const PREVIOUS_DISABLED_ATTRIBUTE = 'data-america250-previous-disabled';
const PREVIOUS_ARIA_LABEL_ATTRIBUTE = 'data-america250-previous-aria-label';
const SELECTOR_PREVIOUS_DISABLED_ATTRIBUTE = 'data-america250-selector-previous-disabled';
const INITIALIZED_ATTRIBUTE = 'data-america-250-direct-checkout-initialized';
const AUTO_STARTED_ATTRIBUTE = 'data-america-250-direct-checkout-auto-started';
const ORIGINAL_SUBMIT_HANDLER_ATTRIBUTE = 'data-america-250-original-submit-handler';
const AUTO_CHECKOUT_PARAM = 'america250_checkout';
const AUTO_CHECKOUT_LOADER_ID = 'America250DirectCheckoutLoader';
const AUTO_CHECKOUT_LOADER_STYLE_ID = 'America250DirectCheckoutLoaderStyles';
const AUTO_CHECKOUT_LOADER_VISIBLE_ATTRIBUTE = 'data-america-250-direct-checkout-loader-visible';
const AUTO_CHECKOUT_LOADER_STATE_ATTRIBUTE = 'data-america-250-direct-checkout-loader-state';
const AUTO_CHECKOUT_LOADER_BODY_CLASS = 'america-250-direct-checkout-loader-active';
const pendingAddedStateObservers = new WeakMap();

function isAmerica250ProductDetails(productDetails) {
  return Boolean(productDetails?.querySelector('.product-patriotic-bundle-offer'));
}

function normalizeCloudConfig(config) {
  const productHandle = typeof config?.productHandle === 'string' ? config.productHandle : '';
  const variantId = Number(config?.variantId);
  const sellingPlanId = Number(config?.sellingPlanId);

  if (!config?.enabled || !productHandle || !Number.isFinite(variantId) || variantId <= 0) {
    return null;
  }

  if (!Number.isFinite(sellingPlanId) || sellingPlanId <= 0) {
    return null;
  }

  return {
    enabled: true,
    productHandle,
    productId: config.productId,
    variantId,
    sellingPlanId,
  };
}

function getCloudProductConfig() {
  const explicitConfig = normalizeCloudConfig(window.legacybox?.cloudProduct);
  if (explicitConfig) return explicitConfig;

  const configElements = document.querySelectorAll(CLOUD_CONFIG_SELECTOR);

  for (const configElement of configElements) {
    try {
      const normalizedConfig = normalizeCloudConfig(JSON.parse(configElement.textContent || '{}'));

      if (normalizedConfig) return normalizedConfig;
    } catch {
      continue;
    }
  }

  return null;
}

function buildCloudCartItem(localId, parentId, cloudConfig) {
  if (!cloudConfig?.enabled) return null;

  return {
    id: Number(cloudConfig.variantId),
    properties: {
      _localId: localId,
      _cloud_product_handle: cloudConfig.productHandle || '',
      _cloud_variant_id: String(cloudConfig.variantId),
      _cloud_selling_plan_id: String(cloudConfig.sellingPlanId),
    },
    quantity: 1,
    ...(parentId ? { parent_id: Number(parentId) } : {}),
    selling_plan: Number(cloudConfig.sellingPlanId),
  };
}

function getFreeProcessingDiscountCode(variantId) {
  if (!variantId) return null;

  const override =
    typeof Theme !== 'undefined'
      ? Theme.legacyBox?.discounts?.overrides?.find(
          (candidate) =>
            candidate.free_processing &&
            candidate.auto_discount &&
            candidate.variant_id.includes(`${variantId}`)
        )
      : null;

  return override?.auto_discount ?? null;
}

function ensureAutoCheckoutLoaderStyles() {
  if (document.getElementById(AUTO_CHECKOUT_LOADER_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = AUTO_CHECKOUT_LOADER_STYLE_ID;
  style.textContent = `
    body.${AUTO_CHECKOUT_LOADER_BODY_CLASS} {
      overflow: hidden;
    }

    .america-250-direct-checkout-loader {
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      display: grid;
      place-items: center;
      box-sizing: border-box;
      min-height: 100dvh;
      padding: clamp(24px, 6vw, 72px);
      background: var(--america-250-loader-background, var(--color-background, rgb(255 255 255)));
      color: var(--america-250-loader-text, rgb(22 51 80));
      font-family:
        var(--font-paragraph--family, var(--font-body--family, system-ui)),
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        sans-serif;
      opacity: 0;
      pointer-events: none;
      transition:
        opacity 180ms ease,
        visibility 180ms ease;
      visibility: hidden;
    }

    .america-250-direct-checkout-loader[${AUTO_CHECKOUT_LOADER_VISIBLE_ATTRIBUTE}] {
      opacity: 1;
      pointer-events: auto;
      visibility: visible;
    }

    .america-250-direct-checkout-loader *,
    .america-250-direct-checkout-loader *::before,
    .america-250-direct-checkout-loader *::after {
      box-sizing: inherit;
    }

    .america-250-direct-checkout-loader__content {
      display: grid;
      justify-items: center;
      gap: 18px;
      width: min(100%, 32rem);
      text-align: center;
    }

    .america-250-direct-checkout-loader__mark {
      position: relative;
      display: grid;
      place-items: center;
      width: 76px;
      height: 76px;
      color: var(--america-250-loader-accent, rgb(149 56 47));
    }

    .america-250-direct-checkout-loader__spinner {
      position: absolute;
      inset: 0;
      border: 3px solid rgb(22 51 80 / 0.16);
      border-radius: 999px;
      border-top-color: currentcolor;
      animation: america-250-direct-checkout-loader-spin 800ms linear infinite;
    }

    .america-250-direct-checkout-loader__badge {
      display: grid;
      place-items: center;
      width: 52px;
      height: 52px;
      border: 1px solid rgb(22 51 80 / 0.16);
      border-radius: 999px;
      background: rgb(255 255 255 / 0.92);
      color: var(--america-250-loader-text, rgb(22 51 80));
      font-size: 1rem;
      font-weight: 800;
      line-height: 1;
    }

    .america-250-direct-checkout-loader__title {
      margin: 0;
      color: var(--america-250-loader-text, rgb(22 51 80));
      font-size: 1.5rem;
      font-weight: 800;
      letter-spacing: 0;
      line-height: 1.1;
    }

    .america-250-direct-checkout-loader__message {
      max-width: 30rem;
      margin: 0;
      color: rgb(44 44 44 / 0.82);
      font-size: 1rem;
      line-height: 1.5;
    }

    @media screen and (min-width: 750px) {
      .america-250-direct-checkout-loader__title {
        font-size: 2rem;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .america-250-direct-checkout-loader {
        transition: none;
      }

      .america-250-direct-checkout-loader__spinner {
        animation-duration: 1.4s;
      }
    }

    @keyframes america-250-direct-checkout-loader-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `;

  document.head.append(style);
}

function getOrCreateAutoCheckoutLoader() {
  const existingLoader = document.getElementById(AUTO_CHECKOUT_LOADER_ID);
  if (existingLoader instanceof HTMLElement) return existingLoader;

  ensureAutoCheckoutLoaderStyles();

  const loader = document.createElement('div');
  loader.id = AUTO_CHECKOUT_LOADER_ID;
  loader.className = 'america-250-direct-checkout-loader';
  loader.setAttribute('aria-busy', 'true');
  loader.setAttribute('aria-live', 'polite');
  loader.setAttribute('role', 'status');
  loader.innerHTML = `
    <div class="america-250-direct-checkout-loader__content">
      <div class="america-250-direct-checkout-loader__mark" aria-hidden="true">
        <span class="america-250-direct-checkout-loader__spinner"></span>
        <span class="america-250-direct-checkout-loader__badge">250</span>
      </div>
      <p class="america-250-direct-checkout-loader__title" data-america-250-loader-title></p>
      <p class="america-250-direct-checkout-loader__message" data-america-250-loader-message></p>
    </div>
  `;

  document.body.append(loader);
  return loader;
}

function syncAutoCheckoutLoaderTheme(loader, productDetails) {
  const offer = productDetails.querySelector('.product-patriotic-bundle-offer');
  if (!(offer instanceof HTMLElement)) return;

  const styles = window.getComputedStyle(offer);
  const colorMap = {
    '--america-250-loader-accent': styles.getPropertyValue('--pdp-250-accent'),
    '--america-250-loader-background': styles.getPropertyValue('--pdp-250-card-background'),
    '--america-250-loader-text': styles.getPropertyValue('--pdp-250-navy'),
  };

  Object.entries(colorMap).forEach(([name, value]) => {
    const normalizedValue = value.trim();
    if (normalizedValue) {
      loader.style.setProperty(name, normalizedValue);
    }
  });
}

function setAutoCheckoutLoaderState(productDetails, state, messages) {
  const loader = getOrCreateAutoCheckoutLoader();
  const title = loader.querySelector('[data-america-250-loader-title]');
  const message = loader.querySelector('[data-america-250-loader-message]');

  syncAutoCheckoutLoaderTheme(loader, productDetails);
  loader.setAttribute(AUTO_CHECKOUT_LOADER_STATE_ATTRIBUTE, state);
  loader.setAttribute('aria-busy', 'true');

  if (title) {
    title.textContent =
      state === 'checkout' ? messages.loaderCheckoutTitle : messages.loaderCartTitle;
  }

  if (message) {
    message.textContent =
      state === 'checkout' ? messages.loaderCheckoutMessage : messages.loaderCartMessage;
  }

  document.body.classList.add(AUTO_CHECKOUT_LOADER_BODY_CLASS);
  requestAnimationFrame(() => {
    loader.setAttribute(AUTO_CHECKOUT_LOADER_VISIBLE_ATTRIBUTE, '');
  });
}

function hideAutoCheckoutLoader() {
  const loader = document.getElementById(AUTO_CHECKOUT_LOADER_ID);
  if (!(loader instanceof HTMLElement)) return;

  loader.removeAttribute(AUTO_CHECKOUT_LOADER_VISIBLE_ATTRIBUTE);
  loader.setAttribute('aria-busy', 'false');
  document.body.classList.remove(AUTO_CHECKOUT_LOADER_BODY_CLASS);
}

function buildCartPayload(productDetails, america250CartConfig) {
  const items = [];
  const localId = Math.floor(Math.random() * 1000000);
  const activeDivs = productDetails.querySelectorAll('[data-active-kit="true"]');
  const parentId = productDetails
    .querySelector('[ref="cards[]"][data-active-kit="true"]')
    ?.getAttribute('data-var-id');
  const parentVariantId = Number(parentId);

  if (!Number.isFinite(parentVariantId) || parentVariantId <= 0) {
    return null;
  }

  if (america250CartConfig?.manageCartContents) {
    const configured = buildAmerica250CartItems(localId, parentId, america250CartConfig, productDetails);
    if (!configured.valid) return null;
    const processingCode = america250CartConfig.cartContents?.processing?.mode !== 'none'
      ? getFreeProcessingDiscountCode(parentId) : null;
    return { items: [{
      id: parentVariantId, quantity: 1,
      properties: { _localId: localId, ...(processingCode ? { _freeProcessingDiscountCode: processingCode } : {}) },
    }, ...configured.items] };
  }

  const freeProcessingDiscountCode = getFreeProcessingDiscountCode(parentId);
  const cloudConfig = getCloudProductConfig();

  activeDivs.forEach((div) => {
    const varId = div.getAttribute('data-var-id');

    items.push({
      id: varId ? Number(varId) : undefined,
      properties: {
        _localId: localId,
        ...(freeProcessingDiscountCode && parentId === varId
          ? { _freeProcessingDiscountCode: freeProcessingDiscountCode }
          : {}),
      },
      quantity: 1,
      ...(parentId && parentId !== varId ? { parent_id: Number(parentId) } : {}),
    });
  });

  const cloudItem = buildCloudCartItem(localId, parentId, cloudConfig);
  if (cloudItem) {
    items.push(cloudItem);
  }

  const america250CartItems = buildAmerica250CartItems(
    localId,
    parentId,
    america250CartConfig,
    productDetails
  );

  items.push(...america250CartItems.items);

  if (!america250CartItems.hasProcessingOverride) {
    const selectedProcessingTime = window.legacybox?.selectedProcessingTime;

    if (selectedProcessingTime?.id) {
      items.push({
        id: Number(selectedProcessingTime.id),
        properties: {
          _localId: localId,
        },
        quantity: 1,
        ...(parentId && parentId !== selectedProcessingTime.id
          ? { parent_id: Number(parentId) }
          : {}),
      });
    } else {
      const standardId = productDetails
        .querySelector('[data-digitizing-time] [data-time="standard"]')
        ?.getAttribute('data-time-id');

      if (standardId) {
        items.push({
          id: Number(standardId),
          properties: {
            _localId: localId,
          },
          quantity: 1,
          ...(parentId && parentId !== standardId ? { parent_id: Number(parentId) } : {}),
        });
      }
    }
  }

  return { items };
}

async function clearCartForBundle() {
  // A background refresh can replace Theme.cart with an older snapshot. Always
  // clear on this explicit purchase action and require Shopify to confirm it.
  const response = await fetch('/cart/clear.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Unable to clear the cart for the America 250 bundle.');
  const cart = await response.json();
  if (cart.item_count !== 0) throw new Error('The cart was not emptied for the America 250 bundle.');
  Theme.legacyBox.cart = cart;
}

function assertBundleCart(cart, payload) {
  const quantities = items => {
    const result = new Map();
    for (const item of items) {
      const id = String(item.variant_id ?? item.id);
      result.set(id, (result.get(id) ?? 0) + item.quantity);
    }
    return result;
  };
  const expected = quantities(payload.items);
  const actual = quantities(cart.items ?? []);
  if (actual.size !== expected.size || [...expected].some(([id, quantity]) => actual.get(id) !== quantity)) {
    throw new Error('The cart does not match the selected America 250 bundle. Please try again.');
  }
}

function clearStoredDiscountState() {
  if (typeof Theme === 'undefined') return;

  const { sessionKey } = Theme.legacyBox.discounts;

  sessionStorage.removeItem(sessionKey);
  // Keep the public offer for a regular PDP restored from browser history.
}

async function clearCartDiscounts() {
  clearStoredDiscountState();

  const response = await fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: '' }),
  });
  if (!response.ok) throw new Error('Unable to reset discounts for the America 250 bundle.');
  const cart = await response.json();
  if (!Number.isInteger(cart.item_count)) throw new Error('Invalid cart response while resetting bundle discounts.');
  Theme.legacyBox.cart = cart;
}

function getPendingButtons(productDetails) {
  const buttons = [
    ...productDetails.querySelectorAll('product-form-component button[ref="addToCartButton"]'),
  ];
  const stickyButton = productDetails.closest?.('.shopify-section')
    ?.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
  if (stickyButton instanceof HTMLButtonElement) {
    buttons.push(stickyButton);
  }

  return buttons.filter((button) => button instanceof HTMLButtonElement);
}

function suppressProductFormButtonAnimation(productDetails) {
  productDetails
    .querySelectorAll('product-form-component button[ref="addToCartButton"]')
    .forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.dataset.puppet = 'true';
        button.removeAttribute('data-added');
      }
    });
}

function suppressPendingAddedState(button) {
  button.removeAttribute('data-added');

  if (pendingAddedStateObservers.has(button)) return;

  const observer = new MutationObserver(() => {
    if (button.getAttribute(PENDING_BUTTON_ATTRIBUTE) === 'true') {
      button.removeAttribute('data-added');
    }
  });

  observer.observe(button, { attributes: true, attributeFilter: ['data-added'] });
  pendingAddedStateObservers.set(button, observer);
}

function releasePendingAddedState(button) {
  const observer = pendingAddedStateObservers.get(button);
  if (!observer) return;

  observer.disconnect();
  pendingAddedStateObservers.delete(button);
}

function setButtonPending(button, isPending, messages = {}) {
  if (isPending) {
    const pendingLabel = button.closest('sticky-add-to-cart')
      ? messages.stickyPendingButtonLabel
      : messages.pendingButtonLabel;
    const accessiblePendingLabel =
      pendingLabel || button.getAttribute('aria-label') || button.textContent?.trim() || '';

    button.setAttribute(PREVIOUS_DISABLED_ATTRIBUTE, button.disabled ? 'true' : 'false');
    button.setAttribute(PREVIOUS_ARIA_LABEL_ATTRIBUTE, button.getAttribute('aria-label') || '');
    button.setAttribute(PENDING_BUTTON_ATTRIBUTE, 'true');
    button.setAttribute(PENDING_BUTTON_LABEL_ATTRIBUTE, accessiblePendingLabel);
    button.setAttribute('aria-label', accessiblePendingLabel);
    button.setAttribute('aria-busy', 'true');
    suppressPendingAddedState(button);
    button.disabled = true;
    return;
  }

  const wasDisabled = button.getAttribute(PREVIOUS_DISABLED_ATTRIBUTE) === 'true';
  const previousAriaLabel = button.getAttribute(PREVIOUS_ARIA_LABEL_ATTRIBUTE);

  button.removeAttribute(PENDING_BUTTON_ATTRIBUTE);
  button.removeAttribute(PENDING_BUTTON_LABEL_ATTRIBUTE);
  button.removeAttribute(PREVIOUS_DISABLED_ATTRIBUTE);
  button.removeAttribute(PREVIOUS_ARIA_LABEL_ATTRIBUTE);
  button.removeAttribute('aria-busy');
  button.removeAttribute('data-added');
  releasePendingAddedState(button);
  button.disabled = wasDisabled;

  if (previousAriaLabel) {
    button.setAttribute('aria-label', previousAriaLabel);
  } else {
    button.removeAttribute('aria-label');
  }
}

function setPending(productDetails, isPending, messages = {}) {
  productDetails.classList.toggle(PENDING_CLASS, isPending);
  getPendingButtons(productDetails).forEach((button) =>
    setButtonPending(button, isPending, messages)
  );

  productDetails.querySelectorAll('[data-patriotic-bundle-option]').forEach((option) => {
    if (!(option instanceof HTMLButtonElement)) return;

    if (isPending) {
      option.setAttribute(SELECTOR_PREVIOUS_DISABLED_ATTRIBUTE, option.disabled ? 'true' : 'false');
      option.disabled = true;
      option.setAttribute('aria-disabled', 'true');
      return;
    }

    const wasDisabled = option.getAttribute(SELECTOR_PREVIOUS_DISABLED_ATTRIBUTE) === 'true';
    option.removeAttribute(SELECTOR_PREVIOUS_DISABLED_ATTRIBUTE);
    option.disabled = wasDisabled;

    if (!wasDisabled) {
      option.removeAttribute('aria-disabled');
    }
  });
}

function dispatchCartError(productFormComponent, form, message, description, errors = null) {
  const errorElement = productFormComponent.querySelector('[ref="addToCartTextError"]');
  if (errorElement) {
    errorElement.classList.remove('hidden');

    const textNode = errorElement.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      errorElement.appendChild(document.createTextNode(message));
    }
  }

  const liveRegion = productFormComponent.querySelector('[ref="liveRegion"]');
  if (liveRegion) {
    liveRegion.textContent = message;
  }

  productFormComponent.dispatchEvent(
    new CartErrorEvent(form.getAttribute('id') || '', message, description, errors)
  );
}

async function runDirectCheckout(productDetails, productFormComponent, form, options = {}) {
  if (productFormComponent.dataset.america250Submitting === 'true') return;

  const america250CartConfig = getAmerica250CartConfig(productDetails);
  const checkoutUrl = getAmerica250CheckoutUrl(america250CartConfig, productDetails);

  if (!checkoutUrl) return;

  const messages = america250CartConfig.messages;

  const cartPayload = buildCartPayload(productDetails, america250CartConfig);

  if (!cartPayload) {
    dispatchCartError(
      productFormComponent,
      form,
      messages.selectionErrorMessage,
      messages.selectionErrorDescription
    );
    return;
  }

  productFormComponent.dataset.america250Submitting = 'true';
  setPending(productDetails, true, messages);
  if (options.showAutoCheckoutLoader) {
    setAutoCheckoutLoaderState(productDetails, 'cart', messages);
  }
  let isRedirecting = false;

  try {
    await clearCartDiscounts();
    await clearCartForBundle();

    const cartAddUrl = typeof Theme !== 'undefined' ? Theme.routes.cart_add_url : '/cart/add.js';
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(cartPayload),
    });
    const textBody = await response.text();
    const json = textBody ? JSON.parse(textBody) : {};

    if (!response.ok || json.status) {
      console.error('America 250 cart add failed:', json);
      dispatchCartError(
        productFormComponent,
        form,
        messages.cartErrorMessage,
        messages.cartErrorDescription
      );
      return;
    }

    // Checkout URL parameters alone do not record why the bundle code was selected.
    // Persist that origin after the bundle exists so returning storefront visits recover it.
    const code = new URL(checkoutUrl, window.location.origin).searchParams.get('discount')?.split(',')[0];
    let confirmedCart;
    if (code) {
      confirmedCart = await updatePurchaseDiscount({ url: Theme.routes.cart_update_url,
        code, applied: 'override_applied' });
    } else {
      const cartResponse = await fetch('/cart.js', { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Unable to confirm the America 250 cart.');
      confirmedCart = await cartResponse.json();
    }
    assertBundleCart(confirmedCart, cartPayload);
    Theme.legacyBox.cart = confirmedCart;
    if (code) {
      // A cached page will not rerun bootstrap. Preserve confirmed ownership in
      // session storage too. An opaque app record requests metadata without inventing a quote.
      sessionStorage.setItem(Theme.legacyBox.discounts.sessionKey, JSON.stringify({
        code, applied: 'override_applied', status: 'success', type: 'DiscountCodeApp',
      }));
    }
    isRedirecting = true;
    if (options.showAutoCheckoutLoader) {
      setAutoCheckoutLoaderState(productDetails, 'checkout', messages);
    }
    const confirmedCheckoutUrl = new URL(checkoutUrl, window.location.origin);
    if (code) {
      confirmedCheckoutUrl.searchParams.set('discount', confirmedCart.discount_codes
        .filter(discount => discount.applicable).map(discount => discount.code).join(','));
    }
    window.location.assign(confirmedCheckoutUrl.href);
  } catch (error) {
    console.error(error);
    dispatchCartError(
      productFormComponent,
      form,
      messages.cartErrorMessage,
      messages.cartErrorDescription
    );
  } finally {
    if (!isRedirecting) {
      delete productFormComponent.dataset.america250Submitting;
      setPending(productDetails, false, messages);
      if (options.showAutoCheckoutLoader) {
        hideAutoCheckoutLoader();
      }
    }
  }
}

async function handleDirectCheckoutSubmit(event, productDetails, productFormComponent, form) {
  event.preventDefault();
  event.stopPropagation();

  await runDirectCheckout(productDetails, productFormComponent, form);
}

function shouldAutoStartDirectCheckout() {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(AUTO_CHECKOUT_PARAM);

  return value === '1' || value === 'true';
}

function autoStartDirectCheckout(productDetails, productFormComponent, form) {
  if (!shouldAutoStartDirectCheckout()) return;
  if (productDetails.hasAttribute(AUTO_STARTED_ATTRIBUTE)) return;

  productDetails.setAttribute(AUTO_STARTED_ATTRIBUTE, 'true');
  void runDirectCheckout(productDetails, productFormComponent, form, {
    showAutoCheckoutLoader: true,
  });
}

function initializeProductDetails(productDetails) {
  if (!(productDetails instanceof HTMLElement)) return;
  if (!isAmerica250ProductDetails(productDetails)) return;

  suppressProductFormButtonAnimation(productDetails);

  if (productDetails.hasAttribute(INITIALIZED_ATTRIBUTE)) return;

  const america250CartConfig = getAmerica250CartConfig(productDetails);
  if (!getAmerica250CheckoutUrl(america250CartConfig, productDetails)) return;

  const productFormComponent = productDetails.querySelector('product-form-component');
  const form = productFormComponent?.querySelector('form');

  if (!(productFormComponent instanceof HTMLElement) || !(form instanceof HTMLFormElement)) {
    return;
  }

  const originalSubmitHandler = productFormComponent.getAttribute('on:submit');
  if (originalSubmitHandler) {
    productFormComponent.setAttribute(ORIGINAL_SUBMIT_HANDLER_ATTRIBUTE, originalSubmitHandler);
    productFormComponent.removeAttribute('on:submit');
  }

  productFormComponent.addEventListener('submit', (event) => {
    handleDirectCheckoutSubmit(event, productDetails, productFormComponent, form);
  });
  productDetails.setAttribute(INITIALIZED_ATTRIBUTE, 'true');

  autoStartDirectCheckout(productDetails, productFormComponent, form);
}

function initializeAmerica250DirectCheckout() {
  document.querySelectorAll('product-details').forEach(initializeProductDetails);
}

initializeAmerica250DirectCheckout();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAmerica250DirectCheckout, { once: true });
}

const observer = new MutationObserver((mutations) => {
  if (
    mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node instanceof Element &&
          (node.matches('product-form-component, product-details') ||
            Boolean(node.querySelector('product-form-component, product-details')))
      )
    )
  ) {
    initializeAmerica250DirectCheckout();
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  hideAutoCheckoutLoader();

  document.querySelectorAll(`.${PENDING_CLASS}`).forEach((productDetails) => {
    if (productDetails instanceof HTMLElement) {
      const productFormComponent = productDetails.querySelector('product-form-component');
      if (productFormComponent instanceof HTMLElement) {
        delete productFormComponent.dataset.america250Submitting;
      }
      setPending(productDetails, false);
    }
  });
});
